import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { PointerEvent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { portalMessages as m } from "./messages";

const WIDTH = 480;
const HEIGHT = 160;

/** A box to draw the rubrica with a finger, pen or mouse. Calls ``onChange``
 * with a PNG data URL once something is drawn, or null when cleared. */
export const SignaturePad = ({ onChange }: { onChange: (drawing: string | null) => void }) => {
  const intl = useIntl();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const strokes = useRef(0);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const context = canvas.current?.getContext("2d");

    if (context) {
      context.lineWidth = 2.5;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.strokeStyle = "#111";
    }
  }, []);

  const point = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();

    return {
      x: ((event.clientX - rect.left) / rect.width) * WIDTH,
      y: ((event.clientY - rect.top) / rect.height) * HEIGHT,
    };
  };

  const start = (event: PointerEvent<HTMLCanvasElement>) => {
    const context = canvas.current?.getContext("2d");

    if (!context) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;

    const { x, y } = point(event);

    context.beginPath();
    context.moveTo(x, y);
  };

  const move = (event: PointerEvent<HTMLCanvasElement>) => {
    const context = canvas.current?.getContext("2d");

    if (!drawing.current || !context) {
      return;
    }

    const { x, y } = point(event);

    context.lineTo(x, y);
    context.stroke();
    strokes.current += 1;
  };

  const end = () => {
    if (!drawing.current) {
      return;
    }

    drawing.current = false;

    // A dot or a tiny scribble is not a rubrica.
    if (strokes.current > 8 && canvas.current) {
      setEmpty(false);
      onChange(canvas.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const context = canvas.current?.getContext("2d");

    context?.clearRect(0, 0, WIDTH, HEIGHT);
    strokes.current = 0;
    setEmpty(true);
    onChange(null);
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Text size={2} color="default2">
        {intl.formatMessage(m.drawRubrica)}
      </Text>
      <canvas
        ref={canvas}
        width={WIDTH}
        height={HEIGHT}
        className="portal-signature"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <Box>
        <Button size="small" variant="tertiary" disabled={empty} onClick={clear}>
          {intl.formatMessage(m.clearRubrica)}
        </Button>
      </Box>
    </Box>
  );
};
